/** Model-source validation and evaluation stay in the client; geometry and baking use the shared engine. */
import type { Scene } from '@babylonjs/core/scene';
import { createMeshBuild, MAX_BUILD_MS } from 'trace-viewer/build';
import type { MeshBuildResult } from 'trace-viewer/build';
export { MAX_TRIANGLES, MAX_BUILD_MS } from 'trace-viewer/build';
export type { MeshBuildStats, MeshBuildResult } from 'trace-viewer/build';
import { instrumentLoopGuards, SHADOWED_GLOBALS, validateSource } from './sandbox-guards';

const MAX_LOOP_ITERATIONS = 2_000_000;

export function describeError(err: unknown, preambleLines: number): string {
    const message = err instanceof Error ? err.message : String(err);
    const stack = err instanceof Error ? (err.stack ?? '') : '';
    const match = /<anonymous>:(\d+):(\d+)/.exec(stack) ?? /Function:(\d+):(\d+)/.exec(stack);
    if (match) {
        const line = Number(match[1]) - preambleLines;
        if (line > 0) return `${message} (line ${line})`;
    }
    return message;
}

/**
 * Where the syntax error is, since V8 will not say.
 *
 * A `SyntaxError` thrown by `new Function` carries NO position: no line, no
 * column, and a stack whose only frame is `at new Function (<anonymous>)`.
 * The model therefore gets the whole of "Unexpected token '.'" about a 7,400-
 * character script and has to re-derive it by eye — which on the `astra-r4`
 * arm it did the only way it could, by rewriting the entire build from
 * scratch: model-bicycle 7,417 chars -> 6,631, arch-chateau-avenue 12,121 ->
 * 11,448, one whole turn each and ~19k output tokens of it. The same sandbox
 * reports a RUNTIME error against the model's own line number (describeError)
 * precisely so it can iterate; a parse error deserves the same.
 *
 * Binary search over line prefixes gives it. The predicate is monotone: a
 * prefix that stops before the offending line either compiles or fails as
 * INCOMPLETE (unclosed brace, unterminated literal), and every prefix that
 * includes it fails outright — so the first prefix to fail for a reason other
 * than running out of input is the line to name. ~10 compiles for a 1,000-
 * line build, and only ever on the failing path.
 */
export function locateSyntaxError(code: string): { line: number; text: string } | null {
    const lines = code.split('\n');
    const fails = (upto: number): boolean => {
        try {
            // eslint-disable-next-line @typescript-eslint/no-implied-eval, no-new-func -- parse probe: compiled, never called
            new Function(lines.slice(0, upto).join('\n'));
            return false;
        } catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            return !/Unexpected end of input|Unterminated/.test(message);
        }
    };
    let lo = 1, hi = lines.length, found = 0;
    while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (fails(mid)) { found = mid; hi = mid - 1; } else lo = mid + 1;
    }
    return found ? { line: found, text: lines[found - 1].trim().slice(0, 120) } : null;
}

/**
 * `...` written as an elision rather than a spread.
 *
 * Both parse failures on the arm are in this family — `f(a, b, ...)` is
 * "Unexpected token ')'" and `mesh..rotation` is "Unexpected token '.'" — and
 * it is the one syntax mistake a model writing a long build under an output
 * cap makes on purpose, as shorthand for "and the rest". Naming it is worth
 * more than the token, because the fix is not a typo correction: it is "write
 * the loop instead".
 */
const ELISION = /\.\.\.\s*[)\]},]|\.\.\.\s*$|[\w)\]]\.\.[\w(]/m;

/** A parse failure, with the line it is on and the line itself. */
export function describeSyntaxError(err: unknown, code: string): string {
    const message = err instanceof Error ? err.message : String(err);
    const at = locateSyntaxError(code);
    const where = at ? `${message} (line ${at.line})\n  ${at.line} | ${at.text}` : message;
    return ELISION.test(code)
        ? `${where}\n\`...\` is not JavaScript. Write the values out, or generate them with a loop — nothing is elided for you.`
        : where;
}

function wrap(instrumented: string): { source: string; preambleLines: number } {
    const preamble = "'use strict';\nvar __loopCount = 0;\n{\n";
    return {
        source: `${preamble}${instrumented}\n}\n`,
        // `new Function(args…, body)` compiles to
        //   function anonymous(a,b,…      <- 1
        //   ) {                           <- 2
        //   <preamble>                    <- 3..n
        //   <model line 1>
        // so the model's line 1 sits two lines below the preamble's own.
        preambleLines: preamble.split('\n').length - 1 + 2,
    };
}

/** Evaluate the existing guarded mesh DSL, then bake it through the shared engine. */
export async function executeMeshCode(code: string, hostScene: Scene, assetName = 'mesh'): Promise<MeshBuildResult> {
    const violations = validateSource(code);
    if (violations.length > 0) return { glb: null, stats: null, errors: violations, warnings: [] };
    const deadline = Date.now() + MAX_BUILD_MS;
    const instrumented = instrumentLoopGuards(code, MAX_LOOP_ITERATIONS,
        `if(Date.now()>${deadline})throw new Error("Build time limit reached (${Math.round(MAX_BUILD_MS / 1000)}s).");`);
    const { source, preambleLines } = wrap(instrumented);
    const session = createMeshBuild(hostScene);
    try {
        const names = Object.keys(session.api);
        let returned: unknown;
        try {
            // eslint-disable-next-line @typescript-eslint/no-implied-eval -- guarded client DSL execution
            const fn = new Function(...SHADOWED_GLOBALS, ...names, source);
            returned = (fn as (...args: unknown[]) => unknown)(
                ...SHADOWED_GLOBALS.map(() => undefined), ...names.map(name => session.api[name]));
        } catch (error) {
            const described = error instanceof SyntaxError
                ? describeSyntaxError(error, code) : describeError(error, preambleLines);
            return { glb: null, stats: null, errors: [described], warnings: session.warnings };
        }
        return await session.bake(returned, assetName);
    } finally {
        session.dispose();
    }
}
